/**
 * 模板处理器
 * 负责处理 {this.xxx} 和 {{xxx}} 模板替换
 */

import type { GetDataFn } from "../types";

/**
 * 模板处理器
 */
export class TemplateProcessor {
  /** 保存文本节点的原始模板（用于重复替换） */
  private originalTemplates: Map<Node, string> = new Map();
  /** 保存元素属性的原始模板 */
  private originalAttributeTemplates: Map<Element, Map<string, string>> =
    new Map();

  /**
   * 处理文本节点和属性模板
   */
  processTextNodes(element: Element | Node, getData: GetDataFn): void {
    // 先处理元素属性
    if (element instanceof Element) {
      this.processAttributes(element, getData);
    }

    // 如果是文本节点，处理文本并返回
    if (element.nodeType === Node.TEXT_NODE) {
      this.processTextContent(element as Text, getData);
      return;
    }

    // 递归处理子节点
    const childNodes = Array.from(element.childNodes);
    for (const child of childNodes) {
      this.processTextNodes(child, getData);
    }
  }

  /**
   * 处理元素属性
   */
  processAttributes(element: Element, getData: GetDataFn): void {
    const attributeTemplates = this.originalAttributeTemplates.get(element);

    if (attributeTemplates) {
      // 已经缓存过原始模板：使用模板计算新值并赋回
      attributeTemplates.forEach((originalTemplate, attrName) => {
        if (element.hasAttribute(attrName)) {
          const newValue = this.replaceTemplate(originalTemplate, getData);
          element.setAttribute(attrName, newValue);
        }
      });
    } else {
      // 第一次遇到该元素：扫描属性并缓存含模板的属性
      const attributes = Array.from(element.attributes);
      const newAttributeTemplates = new Map<string, string>();

      for (const attr of attributes) {
        if (attr.value.includes("{this.") || attr.value.includes("{{")) {
          newAttributeTemplates.set(attr.name, attr.value);
          const newValue = this.replaceTemplate(attr.value, getData);
          if (newValue !== attr.value) {
            element.setAttribute(attr.name, newValue);
          }
        }
      }

      if (newAttributeTemplates.size > 0) {
        this.originalAttributeTemplates.set(element, newAttributeTemplates);
      }
    }

    this.cleanupAttributeTemplates(element);
  }

  /**
   * 清理已不存在的属性记录
   */
  private cleanupAttributeTemplates(element: Element): void {
    const attributeTemplates = this.originalAttributeTemplates.get(element);
    if (!attributeTemplates) {
      return;
    }

    const attributesToRemove: string[] = [];
    attributeTemplates.forEach((_original, attrName) => {
      if (!element.hasAttribute(attrName)) {
        attributesToRemove.push(attrName);
      }
    });

    for (const name of attributesToRemove) {
      attributeTemplates.delete(name);
    }

    if (attributeTemplates.size === 0) {
      this.originalAttributeTemplates.delete(element);
    }
  }

  /**
   * 处理文本节点内容
   */
  private processTextContent(textNode: Text, getData: GetDataFn): void {
    const textContent = textNode.textContent;
    const originalTemplate = this.originalTemplates.get(textNode);
    const templateToUse = originalTemplate ?? textContent;

    const hasThis = templateToUse != null && templateToUse.includes("{this.");
    const hasMustache = templateToUse != null && templateToUse.includes("{{");
    if (hasThis || hasMustache) {
      const newContent = this.replaceTemplate(templateToUse as string, getData);

      // 保存原始模板（第一次处理时）
      if (originalTemplate === undefined && textContent) {
        this.originalTemplates.set(textNode, textContent);
      }

      if (newContent !== textNode.textContent) {
        textNode.textContent = newContent;
      }
    }
  }

  /**
   * 替换模板中的变量
   */
  replaceTemplate(text: string, getData: GetDataFn): string {
    let result = text.replace(
      /\{this\.([^}]+)}/g,
      (_match: string, key: string): string => {
        const value = getData(key.trim());
        return value !== undefined ? String(value) : _match;
      },
    );

    result = result.replace(
      /\{\{\s*([^}]+)\s*}}/g,
      (_match: string, raw: string): string => {
        const key = raw.trim().replace(/^this\./, "");
        const value = getData(key);
        return value !== undefined ? String(value) : _match;
      },
    );

    return result;
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    this.originalTemplates.clear();
    this.originalAttributeTemplates.clear();
  }
}
